import type {
  CanonicalSettingsDocument,
  LocalizedStringValue,
  ProductDocument,
  ProductTypeCategoryDocument,
  SiteAwareProductTypeCategoryDocument,
} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'
import {stegaClean} from '@sanity/client/stega'
import {applyTrailingSlashPolicy} from '@/lib/siteMetadata'
import {productsIndexPath} from '@/lib/sitePaths'
import {localizedProductTypeLabel, slugifyProductTypeLabel} from '@/lib/productTypeRouting'

const trimSlashes = (value?: string | null) => (value || '').replace(/^\/+|\/+$/g, '')
const cleanString = (value?: string | null) => stegaClean(value || '')

const joinPath = (...parts: Array<string | null | undefined>) => {
  const segments = parts.map((part) => trimSlashes(part)).filter(Boolean)
  return segments.length ? `/${segments.join('/')}` : '/'
}

const canonicalKeyToLocaleField = (
  canonicalKey?: string,
): keyof LocalizedStringValue | undefined => {
  switch (canonicalKey) {
    case 'cs-CZ':
      return 'csCZ'
    case 'da-DK':
      return 'daDK'
    case 'de-DE':
    case 'DE':
      return 'deDE'
    case 'en-CA':
      return 'enCA'
    case 'en-GB':
    case 'GB':
      return 'enGB'
    case 'en-US':
    case 'US':
      return 'enUS'
    case 'es-ES':
    case 'ES':
      return 'esES'
    case 'fi-FI':
    case 'FI':
      return 'fiFI'
    case 'fr-CA':
      return 'frCA'
    case 'fr-FR':
    case 'FR':
      return 'frFR'
    case 'it-IT':
    case 'IT':
      return 'itIT'
    case 'nb-NO':
    case 'NO':
      return 'nbNO'
    case 'nl-NL':
    case 'NL':
      return 'nlNL'
    case 'pl-PL':
    case 'PL':
      return 'plPL'
    case 'sv-SE':
    case 'SE':
      return 'svSE'
    default:
      return undefined
  }
}

const productSlug = (product: ProductDocument): string =>
  cleanString(product.routeSlug) ||
  slugifyProductTypeLabel(cleanString(product.title) || cleanString(product.productNumber) || '') ||
  cleanString(product.productNumber) ||
  ''

export const productTypeSlugForProduct = (
  product: ProductDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
  canonicalKeyOverride?: string,
): string => {
  const locale = effectiveProductRouteLocale({
    site,
    customCanonicalKey: canonicalKeyOverride,
    canonicalSettings,
  })
  const productType = product.productType as
    | ProductTypeCategoryDocument
    | SiteAwareProductTypeCategoryDocument
    | null
    | undefined
  const localeField = canonicalKeyToLocaleField(locale)
  const slugFromLocale = localeField
    ? cleanString(productType?.slugsByLocale?.[localeField])
    : undefined
  const label =
    slugFromLocale ||
    cleanString(productType?.slug) ||
    (productType ? localizedProductTypeLabel(productType, locale) : undefined) ||
    cleanString(productType?.title) ||
    cleanString(product.productTypeKey) ||
    ''
  return slugifyProductTypeLabel(label)
}

export const normalizeMarketKey = (
  rawMarketKey: string | undefined,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string | undefined => {
  if (!rawMarketKey) return undefined

  const normalized = cleanString(rawMarketKey).trim()
  if (!normalized) return undefined

  for (const rule of canonicalSettings?.marketRules || []) {
    if (!rule?.canonicalKey) continue
    if (rule.canonicalKey === normalized) return rule.canonicalKey

    const aliases = Array.isArray(rule.matchAliases) ? rule.matchAliases : []
    if (aliases.some((alias) => alias?.toLowerCase() === normalized.toLowerCase())) {
      return rule.canonicalKey
    }
  }

  return normalized
}

export const localeForCanonicalKey = (
  canonicalKey: string | undefined,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string | undefined => {
  if (!canonicalKey) return undefined
  return resolveProductCanonicalRule(canonicalKey, canonicalSettings)?.locale
}

export const effectiveProductRouteLocale = ({
  site,
  customCanonicalKey,
  canonicalSettings,
}: {
  site: SiteLite
  customCanonicalKey?: string
  canonicalSettings?: CanonicalSettingsDocument | null
}): string => {
  if (customCanonicalKey) {
    return localeForCanonicalKey(customCanonicalKey, canonicalSettings) || site.locale || 'en-GB'
  }

  return site.locale || 'en-GB'
}

export const resolveProductCanonicalRule = (
  marketKey: string | undefined,
  canonicalSettings?: CanonicalSettingsDocument | null,
) => {
  const canonicalKey = normalizeMarketKey(marketKey, canonicalSettings)
  if (!canonicalKey) return undefined

  return (canonicalSettings?.productCanonicalRules || []).find(
    (rule) => rule?.canonicalKey === canonicalKey,
  )
}

export const productBasePathForSite = (
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
  canonicalKeyOverride?: string,
): string => {
  const marketKey =
    cleanString(canonicalKeyOverride) ||
    cleanString(site.marketKeys?.[0]) ||
    cleanString(site.locale)
  const rule = resolveProductCanonicalRule(marketKey, canonicalSettings)

  if (rule?.mode === 'custom') {
    return applyTrailingSlashPolicy(
      joinPath(rule.basePath, rule.productsBasePath || 'products'),
      site.routing.trailingSlashPolicy,
    )
  }

  return productsIndexPath(site)
}

export const productCanonicalPathForSite = (
  product: ProductDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
  canonicalKeyOverride?: string,
): string => {
  const basePath = productBasePathForSite(site, canonicalSettings, canonicalKeyOverride)
  const typeSlug = productTypeSlugForProduct(product, site, canonicalSettings, canonicalKeyOverride)
  return applyTrailingSlashPolicy(
    joinPath(basePath, typeSlug, productSlug(product)),
    site.routing.trailingSlashPolicy,
  )
}

export const productTypeCanonicalPathForSite = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
  locale?: string,
): string => {
  const basePath = productBasePathForSite(site, canonicalSettings)
  const localeField = canonicalKeyToLocaleField(locale || site.locale)
  const localeSlug = localeField ? cleanString(productType.slugsByLocale?.[localeField]) : undefined
  const localizedLabel =
    localeSlug ||
    cleanString(productType.slug) ||
    localizedProductTypeLabel(productType, locale || site.locale) ||
    cleanString(productType.title) ||
    cleanString(productType.key) ||
    ''
  const slug = slugifyProductTypeLabel(localizedLabel)

  return applyTrailingSlashPolicy(joinPath(basePath, slug), site.routing.trailingSlashPolicy)
}

export const customProductBasePathForCanonicalKey = (
  canonicalKey: string | undefined,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string | undefined => {
  if (!canonicalKey) return undefined
  const rule = resolveProductCanonicalRule(canonicalKey, canonicalSettings)
  if (rule?.mode !== 'custom') return undefined
  return joinPath(rule.basePath, rule.productsBasePath || 'products')
}

export const resolveCustomProductRouteContext = (
  routePath: string,
  canonicalSettings?: CanonicalSettingsDocument | null,
): {canonicalKey: string; basePath: string} | null => {
  const candidates = (canonicalSettings?.productCanonicalRules || [])
    .filter((rule) => rule?.mode === 'custom' && rule.canonicalKey)
    .map((rule) => ({
      canonicalKey: rule.canonicalKey as string,
      basePath: joinPath(rule.basePath, rule.productsBasePath || 'products'),
    }))
    .sort((a, b) => b.basePath.length - a.basePath.length)

  for (const candidate of candidates) {
    if (routePath === candidate.basePath || routePath.startsWith(`${candidate.basePath}/`)) {
      return candidate
    }
  }

  return null
}

export const productCanonicalPathCandidates = (
  product: ProductDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string[] => {
  const basePath = productBasePathForSite(site, canonicalSettings)
  const typeSlug = productTypeSlugForProduct(product, site, canonicalSettings)
  const candidates = new Set<string>([
    productCanonicalPathForSite(product, site, canonicalSettings),
  ])
  for (const legacySlugRaw of product.legacyRouteSlugs || []) {
    const legacySlug = cleanString(legacySlugRaw)
    if (!legacySlug) continue
    candidates.add(joinPath(basePath, typeSlug, legacySlug))
  }

  for (const rawMarketKey of product.marketKeys || []) {
    const canonicalKey = normalizeMarketKey(rawMarketKey, canonicalSettings)
    const customBasePath = customProductBasePathForCanonicalKey(canonicalKey, canonicalSettings)
    if (!customBasePath) continue
    const customTypeSlug = productTypeSlugForProduct(product, site, canonicalSettings, canonicalKey)
    const canonicalSlug = productSlug(product)
    if (canonicalSlug) candidates.add(joinPath(customBasePath, customTypeSlug, canonicalSlug))
    for (const legacySlugRaw of product.legacyRouteSlugs || []) {
      const legacySlug = cleanString(legacySlugRaw)
      if (!legacySlug) continue
      candidates.add(joinPath(customBasePath, customTypeSlug, legacySlug))
    }
  }

  return Array.from(candidates)
}

export const productTypeCanonicalPathCandidates = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string[] => {
  const candidates = new Set<string>([
    productTypeCanonicalPathForSite(productType, site, canonicalSettings),
  ])
  const localeField = canonicalKeyToLocaleField(site.locale)
  const localeLegacySlugs = localeField ? productType.legacySlugsByLocale?.[localeField] || [] : []
  const globalLegacySlugs = productType.legacySlugs || []
  const basePath = productBasePathForSite(site, canonicalSettings)
  for (const slugRaw of [...localeLegacySlugs, ...globalLegacySlugs]) {
    const slug = cleanString(slugRaw)
    if (!slug) continue
    candidates.add(joinPath(basePath, slug))
  }

  for (const rawMarketKey of productType.marketKeys || []) {
    const canonicalKey = normalizeMarketKey(rawMarketKey, canonicalSettings)
    const customBasePath = customProductBasePathForCanonicalKey(canonicalKey, canonicalSettings)
    if (!customBasePath) continue
    const canonicalLocaleField = canonicalKeyToLocaleField(
      localeForCanonicalKey(canonicalKey, canonicalSettings) || canonicalKey,
    )
    const label =
      (canonicalLocaleField
        ? cleanString(productType.slugsByLocale?.[canonicalLocaleField])
        : undefined) ||
      (canonicalLocaleField
        ? cleanString(productType.labels?.[canonicalLocaleField])
        : undefined) ||
      cleanString(productType.slug) ||
      localizedProductTypeLabel(productType, site.locale) ||
      cleanString(productType.title) ||
      cleanString(productType.key) ||
      ''
    const slug = slugifyProductTypeLabel(label)
    if (!slug) continue
    candidates.add(joinPath(customBasePath, slug))
    const localeLegacy = canonicalLocaleField
      ? productType.legacySlugsByLocale?.[canonicalLocaleField] || []
      : []
    for (const legacySlugRaw of [...localeLegacy, ...(productType.legacySlugs || [])]) {
      const legacySlug = cleanString(legacySlugRaw)
      if (!legacySlug) continue
      candidates.add(joinPath(customBasePath, legacySlug))
    }
  }

  return Array.from(candidates)
}
